import { NUMBER_OF_SWATCHES_TO_SHOW } from 'commons/constants';

export const getHighlightedOptionColors = (product, items) => {
  // find the item object from Algolia that corresponds to this product
  const algoliaItem = items.find(
    item => String(item.product_id) === String(product.id)
  );

  if (algoliaItem?._highlightResult?.option_color === undefined) {
    return [];
  }

  // creates array of highlighted option colors like ["red", "blue"]
  const matchedColors = [
    ...new Set(
      algoliaItem?._highlightResult?.option_color
        ?.filter(color => color.matchLevel !== 'none')
        ?.map(highlight => highlight.value.replace(/<\/?(mark|em)>/g, '')) // remove <mark> and <em> tags
        ?.flat()
        ?.map(word => word.toLowerCase())
    ),
  ];

  return matchedColors || [];
};

export const getVariantsByMatchedColorFirst = (product, items) => {
  const matchedColors = getHighlightedOptionColors(product, items);

  if (matchedColors.length === 0) {
    return product?.variantsToShow?.length === 0
      ? product?.variants.slice(0, NUMBER_OF_SWATCHES_TO_SHOW)
      : product?.variantsToShow || [];
  }

  const matchedVariants = [
    ...new Map(
      matchedColors.map(color =>
        product.variants.filter(
          variant => variant.color && variant.color === color
        )
      )
    )
      .values()
      .filter(Boolean),
  ];

  const variantsToShow = [
    ...new Map(
      [
        ...(matchedVariants ?? []),
        ...(product?.variantsToShow ?? []),
      ].map(item => [item?.sku, item])
    ).values(),
  ];

  return variantsToShow.length > 0
    ? variantsToShow.slice(0, NUMBER_OF_SWATCHES_TO_SHOW)
    : product?.variantsToShow;
};

/**
 * Re-orders a list of enriched products to match Algolia's ranked hit order,
 * and annotates each product with quickship status and Algolia metadata.
 * Pure function — safe to call from useMemo.
 */
export const buildProductsInAlgoliaOrder = (products, items) => {
  if (!items?.length || !products?.length) {
    return products ?? [];
  }

  const productsByIdMap = new Map();
  products.forEach(product => {
    const idNormalized = String(product.id);
    if (!productsByIdMap.has(idNormalized)) {
      productsByIdMap.set(idNormalized, product);
    }
  });

  return items
    .map(
      ({
        __position,
        __queryID,
        is_in_stock,
        objectID,
        product_id: productIdFromAlgolia,
      }) => {
        const productIdNormalized = String(productIdFromAlgolia);
        const productIdNumber = parseInt(productIdFromAlgolia, 10);
        const product = productsByIdMap.get(productIdNormalized);

        if (!product) {
          return null;
        }

        const isInStock = is_in_stock === true;

        const variantsWithQuickship = (
          product.variantsToShow || []
        ).map(variantData => ({ ...variantData, quickship: isInStock }));
        const variantsWithColor = variantsWithQuickship.filter(
          variant => !!variant?.color
        );
        // Keep at least one variant so we always show price
        const variantsToShow =
          variantsWithColor.length > 0
            ? variantsWithColor
            : variantsWithQuickship.slice(0, 1);

        return {
          ...product,
          quickship: isInStock,
          variants: (product.variants || []).map(variantData => ({
            ...variantData,
            quickship: isInStock,
          })),
          variantsToShow,
          algoliaMetaData: {
            queryID: __queryID ?? '',
            position: __position ?? 0,
            objectID: objectID ?? productIdNumber ?? '',
          },
        };
      }
    )
    .filter(Boolean);
};

export const getColorFilterSignature = uri => {
  const decoded = decodeURIComponent(uri);
  const facetFiltersRaw = decoded
    .split('&')
    .find(param => param.startsWith('facetFilters='));
  const facetFilters = facetFiltersRaw
    ? JSON.parse(facetFiltersRaw.split('=')[1])
    : [];
  const colorFilters = facetFilters
    .flat() // Flatten the array of facet filters
    .filter(filter => filter.startsWith('option_color:'))
    .join(','); // Extract color filters and join them into a string

  return colorFilters ?? '';
};
